import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DonationsActions } from 'redux/actions';
import { numberWithCommas } from 'utils';
import brandRoles from 'utils/brandRoles';
import {
  FONT,
  LineIcon,
  Pagination,
  Section,
  SectionHeader,
  SectionTitle,
  TextButton,
} from './ui';
import styled from '@emotion/styled';
import moment from 'moment';
import { Table } from 'antd';
import Papa from 'papaparse';
import { getDonationMessage } from 'utils/donationMessage';

const PER_PAGE = 10;
// Tabellen im Stil der Projektverwaltung: ruhige Kopfzeile, Linien nur zwischen Zeilen.
const MySupportsWrapper = styled.div`
  .ant-table {
    font-family: ${FONT};
    font-size: 14px;
    color: ${brandRoles.neutral800};
  }

  .ant-table-thead > tr > th {
    padding: 10px 12px;
    background: ${brandRoles.neutral50};
    border-bottom: 1px solid ${brandRoles.neutral200};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${brandRoles.neutral500};

    &::before {
      display: none;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 12px;
    border-bottom: 1px solid ${brandRoles.neutral200};
    vertical-align: top;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${brandRoles.neutral50};
  }

  .cell-main {
    color: ${brandRoles.neutral800};
  }

  .cell-sub {
    font-size: 13px;
    color: ${brandRoles.neutral500};
  }

  /* Nie in die Nachbarspalte laufen: einzeilig, zu Langes wird abgeschnitten */
  .nowrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-tbody > tr > td {
    overflow: hidden;
  }

  .ellipsis {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell-anonym {
    color: ${brandRoles.neutral500};
  }

  .cell-amount {
    font-weight: 600;
    white-space: nowrap;
  }

  .cell-main {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cell-message {
    color: ${brandRoles.neutral700};
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .download {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .patreon {
    margin-top: 32px;
  }
`;

export class DonationsTable extends Component {
  static propTypes = {
    getProjectDonations: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    donations: PropTypes.object.isRequired,
    donationsCsvRecords: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
  };
  state = {
    page: 1,
  };
  componentDidMount() {
    const {
      projectId,
      getProjectDonations,
      getPatreonDonations,
      getProjectDonationsAllRecord,
    } = this.props;
    getPatreonDonations(projectId);
    getProjectDonations(projectId, PER_PAGE, 1, true);
    getProjectDonationsAllRecord(projectId);
  }

  goToPage = (page) => {
    const { projectId, getProjectDonations } = this.props;
    this.setState({ page });
    getProjectDonations(projectId, PER_PAGE, page, true);
  };

  handleDownloadCSV = () => {
    const { patreonDonations } = this.props;

    if (patreonDonations?.donations?.length > 0) {
      // Convert goodies data to CSV format
      const csvData = Papa.unparse(patreonDonations.donations);

      // Create a blob containing the CSV data
      const blob = new Blob([csvData], { type: 'text/csv' });

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'patreon.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  downloadSupporterCSV = () => {
    const { donationsCsvRecords } = this.props;
    if (donationsCsvRecords?.donations?.length > 0) {
      // Only what the donor chose to share: display name instead of real name, no email
      const csvRecords = donationsCsvRecords.donations.map((donation) => ({
        Datum: moment.unix(Number(donation.time)).format('DD.MM.YYYY HH:mm'),
        Anzeigename: donation.display_name || 'Anonym',
        Ort: donation.custom_city || '',
        Betrag: donation.amount,
        Nachricht: getDonationMessage(donation),
      }));
      const csvData = Papa.unparse(csvRecords);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'supporter_list.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  render() {
    const { donations, loading, patreonDonations } = this.props;
    const { page } = this.state;
    const columns = [
      {
        title: 'Datum',
        dataIndex: 'time',
        key: 'time',
        width: 200,
        render: (text) => {
          const time = moment.unix(Number(text));
          return (
            <>
              <div className="cell-main">{time.format('DD.MM.YYYY')}</div>
              <div
                className="cell-sub nowrap"
                title={`${time.format('HH:mm')} · ${time.fromNow()}`}
              >
                {time.format('HH:mm')} · {time.fromNow()}
              </div>
            </>
          );
        },
      },
      {
        title: 'Unterstützer',
        dataIndex: 'custom_name',
        key: 'custom_name',
        width: 240,
        // Only what the donor chose to share in checkout: display name (none for
        // anonymous donations) and self-chosen city. No real name, no email.
        render: (text, record) => (
          <>
            <div className={record.custom_name ? 'cell-main' : 'cell-anonym'}>
              {record.custom_name || 'Anonym'}
            </div>
            {record.custom_city && (
              <div className="cell-sub ellipsis" title={record.custom_city}>
                {record.custom_city}
              </div>
            )}
          </>
        ),
      },
      {
        title: 'Betrag',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',
        width: 100,
        render: (text) => (
          <span className="cell-amount">{numberWithCommas(text)} €</span>
        ),
      },
      {
        title: 'Nachricht',
        dataIndex: 'message',
        key: 'message',
        render: (text, record) => {
          const message = getDonationMessage(record);
          return message ? <div className="cell-message">{message}</div> : null;
        },
      },
    ];
    const columnsPatreon = [
      {
        title: 'Anzeigename',
        dataIndex: 'display_name',
        key: 'display_name',
        render: (text) => text || 'Anonym',
      },
      {
        title: 'Betrag',
        dataIndex: 'amount',
        key: 'amount',
        render: (text) => (
          <p>{text !== null ? numberWithCommas(text) + ' €' : '0 €'}</p>
        ),
      },
      {
        title: 'Spendenart',
        dataIndex: 'donation_type',
        key: 'donation_type',
      },
    ];

    const hasDonations =
      donations && donations.donations && donations.donations.length > 0;

    return (
      <MySupportsWrapper>
        {hasDonations && (
          <Section>
            <SectionHeader>
              <SectionTitle>
                Unterstützer
                <span className="count">
                  {donations.count === 1
                    ? '1 Spende'
                    : `${numberWithCommas(donations.count)} Spenden`}
                </span>
              </SectionTitle>
              <TextButton
                type="button"
                className="download"
                onClick={() => this.downloadSupporterCSV()}
              >
                <LineIcon name="download" size={16} />
                CSV herunterladen
              </TextButton>
            </SectionHeader>
            <Table
              columns={columns}
              tableLayout="fixed"
              dataSource={donations.donations}
              rowKey={(record, index) => record.id || `${record.time}-${index}`}
              loading={loading}
              scroll={{ x: 760 }}
              pagination={false}
            />
            <Pagination
              page={page}
              pages={Math.ceil((donations.count || 0) / PER_PAGE)}
              onChange={this.goToPage}
            />
            {patreonDonations?.count > 0 && (
              <div className="patreon">
                <SectionHeader>
                  <SectionTitle>Patreon-Unterstützer</SectionTitle>
                  <TextButton
                    type="button"
                    className="download"
                    onClick={() => this.handleDownloadCSV()}
                  >
                    <LineIcon name="download" size={16} />
                    CSV herunterladen
                  </TextButton>
                </SectionHeader>
                <Table
                  columns={columnsPatreon}
                  dataSource={patreonDonations?.donations || []}
                  rowKey={(record, index) => record.id || index}
                  scroll={{ x: 560 }}
                />
              </div>
            )}
          </Section>
        )}
      </MySupportsWrapper>
    );
  }
}

const mapStateToProps = (state) => {
  const { loading, donations, donationsCsvRecords, patreonDonations } =
    state.donations;
  return {
    loading,
    donations,
    donationsCsvRecords,
    patreonDonations,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getProjectDonationsAllRecord: (projectId, perPage, page) =>
    dispatch(
      DonationsActions.getProjectDonationsAllRecord(projectId, perPage, page)
    ),
  getProjectDonations: (projectId, perPage, page, includePrivate) =>
    dispatch(
      DonationsActions.getProjectDonations(
        projectId,
        perPage,
        page,
        includePrivate
      )
    ),
  getPatreonDonations: (projectId) =>
    dispatch(DonationsActions.getPatreonDonations(projectId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DonationsTable);
